import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { jwtDecode } from 'jwt-decode';
import { Star, Heart } from "lucide-react";

import BusinessProfilePopup from '../../search/BusinessProfilePopup';
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import styles from './SavedBusinesses.module.css';

const SavedBusinesses = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we were redirected here with a message
    if (location.state?.toastMessage) {
      const { toastMessage, toastType } = location.state;
      
      if (toastType === 'success') toast.success(toastMessage);
      else if (toastType === 'info') toast.info(toastMessage);
      else if (toastType === 'error') toast.error(toastMessage);

      // Clear the state so the toast doesn't appear again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const [username, setUsername] = useState('');
  const [businessUserId, setBusinessUserId] = useState(null);
  const [homeUserId, setHomeUserId] = useState(null);
  const [savedBusinessesData, setSavedBusinessesData] = useState([]);
  const [isLoadingSavedBusinesses, setIsLoadingSavedBusinesses] = useState(false);
  const [savedBusinessesError, setSavedBusinessesError] = useState(null);
  const [myuserType, setMyUserType] = useState(localStorage.getItem('userType'));
  const [loggedInBusinessId, setLoggedInBusinessId] = useState(localStorage.getItem('loggedInBusinessId'));

  // Panel State
  const [isBusinessProfileOpen, setIsBusinessProfileOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUsername(decodedToken.UserName || 'User');
        const userType = decodedToken.UserType;
        setMyUserType(userType);
        if (userType === 'Business') {
          setBusinessUserId(decodedToken.nameid);
          setLoggedInBusinessId(localStorage.getItem('loggedInBusinessId'));
        } else {
          setHomeUserId(decodedToken.nameid);
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchSavedBusinesses = async () => {
      setIsLoadingSavedBusinesses(true);
      setSavedBusinessesError(null);
      
      let url = '';
      if (myuserType === 'Business' && loggedInBusinessId) {
        url = `${API_BASE_URL}/api/BusinessProfile/getSavedBusinesses?userType=Business&userId=${loggedInBusinessId}`;
      } else if (myuserType === 'Home' && homeUserId) {
        url = `${API_BASE_URL}/api/BusinessProfile/getSavedBusinesses?userType=Home&userId=${homeUserId}`;
      }

      if (url) {
        try {
          const response = await axios.get(url);
          setSavedBusinessesData(response.data || []);
        } catch (err) {
          console.error("Error fetching saved businesses:", err);
          setSavedBusinessesError(err.message || "Failed to fetch saved businesses.");
          setSavedBusinessesData([]);
        } finally {
          setIsLoadingSavedBusinesses(false);
        }
      } else {
        setIsLoadingSavedBusinesses(false);
      }
    };

    if ((myuserType === 'Business' && loggedInBusinessId) || (myuserType === 'Home' && homeUserId)) {
      fetchSavedBusinesses();
    }
  }, [myuserType, loggedInBusinessId, homeUserId]);

  const openBusinessProfile = (businessId) => {
    setSelectedBusinessId(businessId);
    setIsBusinessProfileOpen(true);
  };

  const closeBusinessProfile = () => {
    setIsBusinessProfileOpen(false);
    setSelectedBusinessId(null);
  };

  return (
    <div className={styles.pageContainer}>
        
        <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'saved'} />

        {/* GOLD HEADER BAR */}
        <header className={styles.header}>
            <div className={styles.headerContent}>
                <div>
                    <h1 className={styles.headerTitle}>Saved Businesses</h1>
                </div>
            </div>
        </header>

        <div className={styles.mainContainer}>
            
            <div className={styles.welcomeSection}>
                <p className={styles.welcomeSubtitle}>Your shortlisted professionals</p>
            </div>

            {isLoadingSavedBusinesses && <p style={{textAlign:'center'}}>Loading saved businesses...</p>}
            
            {!isLoadingSavedBusinesses && savedBusinessesError && (
                <div className={styles.errorText}>Error: {savedBusinessesError}</div>
            )}
            
            {!isLoadingSavedBusinesses && !savedBusinessesError && savedBusinessesData.length === 0 && (
                <div className={styles.emptyState}>
                    <Heart size={48} style={{opacity:0.2, marginBottom:'1rem'}} />
                    <p>No saved businesses found.</p>
                </div>
            )}

            {!isLoadingSavedBusinesses && !savedBusinessesError && savedBusinessesData.length > 0 && (
                <div className={styles.statsGrid}>
                    {savedBusinessesData.map((business) => (
                        <div key={business.businessId} className={styles.statCard}>
                            <img
                                src={business.profilePic || "/placeholder.svg"}
                                alt={business.businessName}
                                className={styles.cardImage}
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'; }}
                            />
                            
                            <div className={styles.statCardContent}>
                                <div className={styles.savedBusinessInfo}>
                                    <h4 className={styles.savedBusinessName}>{business.businessName}</h4>
                                    
                                    <p className={styles.savedBusinessDescription}>
                                        {business.suburb ? `${business.suburb}, ${business.postCode}` : 'Location hidden'}
                                    </p>

                                    {business.description && (
                                        <p className={styles.savedBusinessDescription}>{business.description}</p>
                                    )}
                                    
                                    <div className={styles.ratingContainer}>
                                        <Star className={styles.ratingStarIcon} />
                                        <span>{business.overallRating !== null ? business.overallRating.toFixed(1) : 'N/A'}</span>
                                    </div>
                                </div>

                                <button 
                                    className={styles.btnOutline} 
                                    onClick={() => openBusinessProfile(business.businessId)}
                                >
                                    View Profile
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {isBusinessProfileOpen && selectedBusinessId && (
            <div className={styles.profileOverlay} onClick={closeBusinessProfile}>
                <div className={styles.profilePopupContainer} onClick={(e) => e.stopPropagation()}>
                    <BusinessProfilePopup
                        key={selectedBusinessId}
                        businessId={selectedBusinessId}
                        onClose={closeBusinessProfile}
                    />
                </div>
            </div>
        )}
    </div>
  );
};

export default SavedBusinesses;
